import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merge Tailwind CSS classes with clsx.
 * Handles conflicts intelligently (e.g., bg-red-500 bg-blue-500 -> bg-blue-500)
 *
 * @example
 * // Basic merge
 * cn('p-4', 'mt-2') // => 'p-4 mt-2'
 *
 * // Conditional classes
 * cn('base', isActive && 'bg-primary') // => 'base bg-primary' or 'base'
 *
 * // Override conflicts
 * cn('bg-red-500', 'bg-blue-500') // => 'bg-blue-500'
 *
 * // With custom className prop
 * cn('default-class', className)
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Extract a human-readable message from an unknown error value.
 *
 * Replaces the repeated `err instanceof Error ? err.message : 'fallback'`
 * pattern used in catch blocks throughout the codebase.
 */
export function getErrorMessage(err: unknown, fallback = 'An unexpected error occurred'): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return fallback;
}

/**
 * Safely convert an unknown value to a display string.
 *
 * Handles null/undefined, primitives, and objects without producing
 * unhelpful "[object Object]" output (uses JSON.stringify for objects).
 */
export function toDisplayString(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') return String(value);
  if (typeof value === 'object') return JSON.stringify(value);
  // value is `symbol | function` here (null/undefined returned at the top,
  // every other type handled above). These are never real display values, and
  // ANY stringification of them (String() OR .toString()) trips S6551 because
  // SonarCloud treats them as object-like — so collapse to '' instead.
  return '';
}

